import React, { useEffect, useId, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, LogOut, Smartphone, UserRound } from 'lucide-react';
import { cn } from '../utils/cn';

export const initials = (name) =>
    String(name || '')
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((w) => w[0].toUpperCase())
        .join('') || '?';

export const roleLabel = (user) => (user?.isPortalAdmin ? 'Portal admin' : 'App admin');

const itemClass =
    'flex min-h-11 w-full items-center gap-2.5 px-3 py-2 text-left text-sm font-medium outline-none transition md:min-h-9';

/**
 * Account menu button (WAI-ARIA menu button): who is signed in, in which app session,
 * and the account actions. Arrow keys move between items, Escape closes and returns
 * focus to the trigger, clicking outside closes.
 */
const AccountMenu = ({ user, sessionLabel, onSignOut, signingOut }) => {
    const [open, setOpen] = useState(false);
    const rootRef = useRef(null);
    const buttonRef = useRef(null);
    const menuRef = useRef(null);
    const navigate = useNavigate();
    const baseId = useId();
    const menuId = `${baseId}-menu`;

    const items = () => Array.from(menuRef.current?.querySelectorAll('[role="menuitem"]:not([disabled])') || []);
    const focusItem = (index) => {
        const list = items();
        if (!list.length) return;
        list[(index + list.length) % list.length].focus();
    };

    const close = (returnFocus = true) => {
        setOpen(false);
        if (returnFocus) buttonRef.current?.focus();
    };

    // Click outside closes (without stealing focus from whatever was clicked).
    useEffect(() => {
        if (!open) return undefined;
        const onDown = (e) => {
            if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', onDown);
        return () => document.removeEventListener('mousedown', onDown);
    }, [open]);

    const openMenu = (focusLast = false) => {
        setOpen(true);
        requestAnimationFrame(() => focusItem(focusLast ? -1 : 0));
    };

    const onButtonKeyDown = (e) => {
        if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            openMenu(false);
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            openMenu(true);
        }
    };

    const onMenuKeyDown = (e) => {
        const list = items();
        const current = list.indexOf(document.activeElement);
        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                focusItem(current + 1);
                break;
            case 'ArrowUp':
                e.preventDefault();
                focusItem(current - 1);
                break;
            case 'Home':
                e.preventDefault();
                focusItem(0);
                break;
            case 'End':
                e.preventDefault();
                focusItem(-1);
                break;
            case 'Escape':
                e.preventDefault();
                e.stopPropagation();
                close(true);
                break;
            case 'Tab':
                setOpen(false);
                break;
            default:
        }
    };

    const go = (to) => {
        setOpen(false);
        navigate(to);
    };

    if (!user) return null;

    return (
        <div ref={rootRef} className="relative">
            <button
                ref={buttonRef}
                type="button"
                aria-haspopup="menu"
                aria-expanded={open}
                aria-controls={open ? menuId : undefined}
                aria-label={`Account: ${user.name}, ${roleLabel(user)}`}
                onClick={() => (open ? close(false) : openMenu(false))}
                onKeyDown={onButtonKeyDown}
                className="flex h-11 items-center gap-2 rounded-lg px-1.5 text-left transition hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-200 md:h-10 md:px-2"
            >
                <span
                    className="grid h-8 w-8 shrink-0 place-content-center rounded-full bg-primary-50 text-xs font-semibold text-primary-700"
                    aria-hidden="true"
                >
                    {initials(user.name)}
                </span>
                <span className="hidden min-w-0 max-w-[12rem] md:block" aria-hidden="true">
                    <span className="block truncate text-sm font-medium leading-5 text-slate-900">{user.name}</span>
                    <span className="block truncate text-xs leading-4 text-slate-600">{roleLabel(user)}</span>
                </span>
                <ChevronDown
                    className={cn('hidden h-4 w-4 shrink-0 text-slate-500 transition md:block', open && 'rotate-180')}
                    aria-hidden="true"
                />
            </button>

            {open && (
                <div className="absolute right-0 top-full z-50 mt-1.5 w-72 max-w-[calc(100vw-2rem)] overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg">
                    <div className="border-b border-slate-200 px-3 py-3">
                        <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                                <p className="truncate text-sm font-semibold text-slate-900" title={user.name}>
                                    {user.name}
                                </p>
                                {user.username && (
                                    <p className="truncate text-xs text-slate-600" title={user.username}>
                                        {user.username}
                                    </p>
                                )}
                                {user.email && user.email !== user.username && (
                                    <p className="truncate text-xs text-slate-600" title={user.email}>
                                        {user.email}
                                    </p>
                                )}
                            </div>
                            <span className="shrink-0 rounded-md border border-slate-200 bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700">
                                {roleLabel(user)}
                            </span>
                        </div>
                        {sessionLabel && (
                            <p className="mt-2 truncate text-xs text-slate-600" title={sessionLabel}>
                                Session: <span className="font-medium text-slate-800">{sessionLabel}</span>
                            </p>
                        )}
                    </div>
                    <div
                        ref={menuRef}
                        id={menuId}
                        role="menu"
                        aria-label="Account"
                        onKeyDown={onMenuKeyDown}
                        className="py-1"
                    >
                        <button
                            type="button"
                            role="menuitem"
                            tabIndex={-1}
                            onClick={() => go('/account')}
                            className={cn(
                                itemClass,
                                'text-slate-800 hover:bg-slate-100 focus-visible:bg-slate-100 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-200'
                            )}
                        >
                            <UserRound className="h-4 w-4 text-slate-500" aria-hidden="true" />
                            Profile &amp; password
                        </button>
                        <button
                            type="button"
                            role="menuitem"
                            tabIndex={-1}
                            onClick={() => go('/account/security')}
                            className={cn(
                                itemClass,
                                'text-slate-800 hover:bg-slate-100 focus-visible:bg-slate-100 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-200'
                            )}
                        >
                            <Smartphone className="h-4 w-4 text-slate-500" aria-hidden="true" />
                            Two-step verification
                        </button>
                        <div role="separator" className="my-1 h-px bg-slate-200" />
                        <button
                            type="button"
                            role="menuitem"
                            tabIndex={-1}
                            disabled={signingOut}
                            aria-disabled={signingOut || undefined}
                            onClick={onSignOut}
                            className={cn(
                                itemClass,
                                'text-red-700 hover:bg-red-50 focus-visible:bg-red-50 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-red-200 disabled:cursor-wait disabled:opacity-60'
                            )}
                        >
                            <LogOut className="h-4 w-4" aria-hidden="true" />
                            {signingOut ? 'Signing out…' : 'Sign out'}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AccountMenu;
